import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Quote, ArrowRight } from "lucide-react";
import PageHero from "@/components/site/PageHero";
import MagneticButton from "@/components/site/MagneticButton";
import { getCaseStudy, getCaseStudies } from "@/lib/api";
import { useSEO } from "@/lib/seo";

function Block({ md = "" }) {
  return (
    <div className="space-y-4 text-inkText-muted leading-[1.8]">
      {md.split(/\n\n+/).map((p, i) => <p key={i}>{p}</p>)}
    </div>
  );
}

export default function CaseStudyDetail() {
  const { slug } = useParams();
  const [cs, setCs] = useState(null);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    setCs(null);
    getCaseStudy(slug).then(setCs).catch(() => setCs({ notFound: true }));
    getCaseStudies().then((all) => setRelated(all.filter((c) => c.slug !== slug).slice(0, 3))).catch(() => {});
  }, [slug]);

  useSEO({
    title: cs?.title ? `${cs.title} | RockLadder Case Study` : "Case Study",
    description: cs?.summary,
    canonical: `https://rockladder.com/case-studies/${slug}`,
  });

  if (cs?.notFound) return <div className="pt-40 pb-24 text-center"><Link to="/case-studies" className="text-gold">Back to Case Studies</Link></div>;
  if (!cs) return <div className="pt-40 pb-24 text-center text-inkText-muted">Loading…</div>;

  return (
    <>
      <PageHero
        eyebrow={cs.industry?.toUpperCase()}
        title={cs.title}
        subtitle={cs.summary}
        breadcrumb={[{ label: "Case Studies", to: "/case-studies" }, { label: cs.title }]}
      />

      {/* Big result numbers */}
      <section className="relative py-8">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <div className="glass-panel-strong rounded-3xl p-8 md:p-12 grid grid-cols-1 md:grid-cols-3 gap-8">
            {(cs.metrics || []).map((m) => (
              <div key={m.label} className="text-center" data-testid={`case-metric-${m.label.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className="font-display leading-none text-gradient-gold" style={{ fontSize: "clamp(2rem, 4vw, 3.5rem)" }}>
                  {m.value}
                </div>
                <div className="mt-4 font-mono text-[10px] tracking-[0.28em] text-inkText-muted">
                  {m.label.toUpperCase()}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="relative py-16">
        <div className="mx-auto max-w-4xl px-6 lg:px-10 space-y-14">
          <div>
            <h2 className="font-display text-2xl md:text-3xl text-inkText-primary mb-5">Challenge</h2>
            <Block md={cs.challenge_md || ""} />
          </div>
          <div>
            <h2 className="font-display text-2xl md:text-3xl text-inkText-primary mb-5">Solution</h2>
            <Block md={cs.solution_md || ""} />
          </div>
          <div>
            <h2 className="font-display text-2xl md:text-3xl text-inkText-primary mb-5">Results</h2>
            <ul className="space-y-3">
              {(cs.results || []).map((r) => (
                <li key={r} className="flex items-start gap-3 glass-panel rounded-xl p-4 text-sm text-inkText-primary/90">
                  <span className="mt-1 h-1.5 w-1.5 rounded-full bg-gold flex-shrink-0" />
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="glass-panel-strong rounded-2xl p-8 md:p-10">
            <Quote className="h-6 w-6 text-gold mb-4" strokeWidth={1.6} />
            <p className="text-lg md:text-xl text-inkText-primary/90 leading-relaxed">
              &ldquo;RockLadder shipped exactly what they committed to — on time, on the number, on the SLA. That was the whole difference.&rdquo;
            </p>
            <div className="mt-6 font-mono text-[10px] tracking-[0.24em] text-inkText-muted">
              {cs.client_name?.toUpperCase()}
            </div>
          </div>

          <div className="glass-panel-strong rounded-2xl p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <div className="font-display text-xl text-inkText-primary">Facing a similar problem?</div>
              <p className="text-sm text-inkText-muted mt-1">30-minute working session — we&apos;ll bring a redacted plan.</p>
            </div>
            <MagneticButton as={Link} to="/contact" variant="primary">
              Book a Consultation <ArrowRight className="h-4 w-4" />
            </MagneticButton>
          </div>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="relative py-16 border-t border-gold/10">
          <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
            <h2 className="font-display text-2xl md:text-3xl text-inkText-primary mb-10">More case studies</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {related.map((r) => (
                <Link key={r.slug} to={`/case-studies/${r.slug}`} className="glass-panel rounded-2xl p-5 hover:border-gold transition-colors">
                  <div className="font-mono text-[10px] tracking-[0.24em] text-gold-body mb-2">{r.industry?.toUpperCase()}</div>
                  <div className="font-display text-base text-inkText-primary line-clamp-2">{r.title}</div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
